import React from "react";
import { Link } from "react-router-dom";
import logo from "../imgs/logo.png";

const GDPRCompliance = () => {
  return (
    <div className="legal-page">
      {/* Navigation Header */}
      <div className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="back-button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Back
          </Link>

          <div className="legal-nav-brand">
            <img src={logo} alt="Viralz Logo" className="legal-nav-logo" />
            <span className="legal-nav-title">Viralz</span>
          </div>
        </div>
      </div>

      <div className="legal-container">
        <h1>GDPR Compliance</h1>
        <div className="legal-meta">
          <p>
            <strong>Effective Date:</strong> January 1, 2025
          </p>
          <p>
            <strong>Last Updated:</strong> January 1, 2025
          </p>
        </div>

        <section>
          <h2>1. INTRODUCTION</h2>
          <p>
            This section outlines how Viralz complies with the General Data
            Protection Regulation (GDPR) for users in the European Union and
            European Economic Area.
          </p>
        </section>

        <section>
          <h2>2. LEGAL BASIS FOR PROCESSING</h2>
          <p>
            We process your personal data based on the following legal grounds:
          </p>

          <h3>2.1 Contract Performance</h3>
          <ul>
            <li>
              <strong>Account Management:</strong> Processing necessary to
              provide our Service
            </li>
            <li>
              <strong>Subscription Services:</strong> Managing your premium
              subscription
            </li>
            <li>
              <strong>Customer Support:</strong> Responding to your inquiries
              and requests
            </li>
          </ul>

          <h3>2.2 Legitimate Interests</h3>
          <ul>
            <li>
              <strong>Service Improvement:</strong> Analyzing usage to enhance
              our Service
            </li>
            <li>
              <strong>Security:</strong> Protecting against fraud and
              unauthorized access
            </li>
            <li>
              <strong>Marketing:</strong> Sending relevant updates and
              promotional content
            </li>
          </ul>

          <h3>2.3 Consent</h3>
          <ul>
            <li>
              <strong>Optional Features:</strong> Additional analytics and
              personalization features
            </li>
            <li>
              <strong>Marketing Communications:</strong> Promotional emails and
              notifications
            </li>
            <li>
              <strong>Cookies:</strong> Non-essential cookies for analytics and
              marketing
            </li>
          </ul>
        </section>

        <section>
          <h2>3. YOUR RIGHTS UNDER GDPR</h2>

          <h3>3.1 Right to Information</h3>
          <ul>
            <li>Clear information about how we process your data</li>
            <li>Details about the purpose and legal basis for processing</li>
            <li>Information about data retention periods</li>
          </ul>

          <h3>3.2 Right of Access</h3>
          <ul>
            <li>Request a copy of your personal data</li>
            <li>Information about how your data is being processed</li>
            <li>Details about data sharing with third parties</li>
          </ul>

          <h3>3.3 Right to Rectification</h3>
          <ul>
            <li>Correct inaccurate or incomplete personal data</li>
            <li>Update your account information</li>
            <li>Modify your preferences and settings</li>
          </ul>

          <h3>3.4 Right to Erasure ("Right to be Forgotten")</h3>
          <ul>
            <li>Request deletion of your personal data</li>
            <li>Permanent account deletion</li>
            <li>Removal from marketing lists</li>
          </ul>

          <h3>3.5 Right to Restrict Processing</h3>
          <ul>
            <li>Limit how we process your personal data</li>
            <li>Suspend processing while disputes are resolved</li>
            <li>Restrict use for specific purposes</li>
          </ul>

          <h3>3.6 Right to Data Portability</h3>
          <ul>
            <li>Receive your data in a structured, machine-readable format</li>
            <li>Transfer your data to another service provider</li>
            <li>Export your TikTok analytics data</li>
          </ul>

          <h3>3.7 Right to Object</h3>
          <ul>
            <li>Object to processing based on legitimate interests</li>
            <li>Opt-out of marketing communications</li>
            <li>Withdraw consent for optional features</li>
          </ul>
        </section>

        <section>
          <h2>4. DATA PROTECTION MEASURES</h2>

          <h3>4.1 Technical Safeguards</h3>
          <ul>
            <li>
              <strong>Encryption:</strong> Data encrypted in transit and at rest
            </li>
            <li>
              <strong>Access Controls:</strong> Role-based access to personal
              data
            </li>
            <li>
              <strong>Regular Backups:</strong> Secure data backup and recovery
              procedures
            </li>
            <li>
              <strong>Security Monitoring:</strong> Continuous monitoring for
              security threats
            </li>
          </ul>

          <h3>4.2 Organizational Measures</h3>
          <ul>
            <li>
              <strong>Staff Training:</strong> Regular privacy and security
              training
            </li>
            <li>
              <strong>Data Protection Policies:</strong> Comprehensive internal
              policies
            </li>
            <li>
              <strong>Incident Response:</strong> Procedures for handling data
              breaches
            </li>
            <li>
              <strong>Vendor Management:</strong> Due diligence on third-party
              processors
            </li>
          </ul>
        </section>

        <section>
          <h2>5. INTERNATIONAL TRANSFERS</h2>
          <p>
            When we transfer your data outside the EU/EEA, we ensure adequate
            protection through:
          </p>
          <ul>
            <li>
              <strong>Adequacy Decisions:</strong> Transfers to countries with
              adequate protection
            </li>
            <li>
              <strong>Standard Contractual Clauses:</strong> EU-approved
              contract terms
            </li>
            <li>
              <strong>Binding Corporate Rules:</strong> Internal data protection
              rules
            </li>
            <li>
              <strong>Certification Schemes:</strong> Third-party privacy
              certifications
            </li>
          </ul>
        </section>

        <section>
          <h2>6. DATA RETENTION</h2>
          <p>We retain your personal data only as long as necessary:</p>
          <ul>
            <li>
              <strong>Account Data:</strong> While your account is active
            </li>
            <li>
              <strong>Legal Obligations:</strong> As required by applicable laws
            </li>
            <li>
              <strong>Legitimate Interests:</strong> For service improvement and
              security
            </li>
            <li>
              <strong>Consent-Based Processing:</strong> Until consent is
              withdrawn
            </li>
          </ul>
        </section>

        <section>
          <h2>7. DATA PROTECTION OFFICER</h2>
          <p>
            For GDPR-related inquiries, you can contact our Data Protection
            Officer:
          </p>
          <ul>
            <li>
              <strong>Email:</strong> viralzmanagement@gmail.com
            </li>
            <li>
              <strong>Response Time:</strong> Within 30 days of receipt
            </li>
          </ul>
        </section>

        <section>
          <h2>8. SUPERVISORY AUTHORITY</h2>
          <p>
            You have the right to lodge a complaint with your local supervisory
            authority if you believe we have violated your data protection
            rights.
          </p>
        </section>

        <section>
          <h2>9. CHILDREN'S DATA</h2>
          <p>
            We do not knowingly process personal data of children under 16
            without parental consent, in accordance with GDPR requirements.
          </p>
        </section>

        <section>
          <h2>10. AUTOMATED DECISION-MAKING</h2>
          <p>We may use automated systems to:</p>
          <ul>
            <li>
              <strong>Content Analysis:</strong> Analyze your TikTok content for
              insights
            </li>
            <li>
              <strong>Recommendations:</strong> Provide personalized content
              suggestions
            </li>
            <li>
              <strong>Performance Scoring:</strong> Calculate profile and
              content scores
            </li>
          </ul>
          <p>
            You have the right to request human review of automated decisions
            that significantly affect you.
          </p>
        </section>

        <section>
          <h2>11. CONTACT US</h2>
          <p>For GDPR-related questions or to exercise your rights:</p>
          <ul>
            <li>
              <strong>Email:</strong> viralzmanagement@gmail.com
            </li>
            <li>
              <strong>Website:</strong> https://viralzapp.com/gdpr
            </li>
            <li>
              <strong>Response Time:</strong> Within 30 days
            </li>
          </ul>
        </section>
      </div>

      <style jsx>{`
        .legal-page {
          min-height: 100vh;
          background: #000000;
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          padding: 0;
        }

        .legal-nav {
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(254, 44, 85, 0.2);
          padding: 20px 0;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .legal-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          padding: 10px 16px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: rgba(254, 44, 85, 0.2);
          border-color: #fe2c55;
          color: #fe2c55;
          transform: translateX(-2px);
        }

        .legal-nav-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .legal-nav-logo {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(254, 44, 85, 0.1);
          padding: 4px;
          border: 1px solid rgba(254, 44, 85, 0.3);
        }

        .legal-nav-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #fe2c55;
        }

        .legal-container {
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
          padding: 40px 20px;
        }

        h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 20px;
          background: linear-gradient(135deg, #fe2c55, #ff6b9d);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .legal-meta {
          margin-bottom: 40px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          border: 1px solid rgba(254, 44, 85, 0.2);
        }

        .legal-meta p {
          margin: 5px 0;
          color: #cccccc;
        }

        section {
          margin-bottom: 40px;
        }

        h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 20px;
          color: #fe2c55;
        }

        h3 {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #ffffff;
        }

        p {
          margin-bottom: 15px;
          color: #cccccc;
        }

        ul {
          margin-bottom: 20px;
          padding-left: 20px;
        }

        li {
          margin-bottom: 10px;
          color: #cccccc;
        }

        strong {
          color: #ffffff;
        }

        @media (max-width: 768px) {
          .legal-container {
            padding: 20px 15px;
          }

          .legal-nav-container {
            padding: 0 15px;
          }

          .legal-nav-title {
            font-size: 1.3rem;
          }

          .legal-nav-logo {
            width: 28px;
            height: 28px;
          }

          .back-button {
            padding: 8px 12px;
            font-size: 0.9rem;
          }

          h1 {
            font-size: 2rem;
          }

          h2 {
            font-size: 1.5rem;
          }

          h3 {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default GDPRCompliance;
